import { setAudioSessionType } from '../../services/micStream'

// Mic state only. ASR is server-side (services/asr.js) and the mic is opened per
// attempt (services/micStream.js): a mic live during playback ducks iOS Safari volume.

// Prefer the Permissions API (no audio-session change). Safari lacks it for
// 'microphone', so open and release a stream once, never while audio plays.
async function probeMicPermission () {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return 'unsupported'
  }

  try {
    if (navigator.permissions && navigator.permissions.query) {
      const status = await navigator.permissions.query({ name: 'microphone' })
      if (status && status.state === 'granted') return 'granted'
      if (status && status.state === 'denied') return 'denied'
    }
  } catch (e) {
  }

  // iOS: a session left on 'playback' rejects capture (InvalidStateError) and
  // survives reloads (process-level), so reset it first, as micStream.start() does.
  setAudioSessionType('auto')

  let stream
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  } catch (e) {
    // ponytail: diagnostic only, names any other getUserMedia rejection reason.
    console.warn('[recognition] mic probe failed: ' + (e && e.name) + ' - ' + (e && e.message))
    return 'denied'
  }
  stream.getTracks().forEach(function (track) { track.stop() })
  return 'granted'
}

export default {
  namespaced: true,

  state: {
    recognizedText: '',
    // Top-nav mic toggle; off means the drill can't run.
    speakingMode: false,
    micDenied: false,
    // Lets startDrill skip the probe after the first sentence.
    micGranted: false,
    micActive: false,
    voiceDetected: false,

    countdownActive: false,
    countdownType: '',
    countdownProgress: 0,
    countdownDuration: 1500
  },

  mutations: {
    SET_RECOGNIZED_TEXT (state, text) {
      state.recognizedText = text || ''
    },

    SET_SPEAKING_MODE (state, enabled) {
      state.speakingMode = !!enabled
    },

    SET_MIC_DENIED (state, val) {
      state.micDenied = !!val
    },

    SET_MIC_GRANTED (state, val) {
      state.micGranted = !!val
    },

    SET_MIC_ACTIVE (state, val) {
      state.micActive = !!val
    },

    SET_VOICE_DETECTED (state, isDetected) {
      state.voiceDetected = !!isDetected
    },

    SET_COUNTDOWN (state, { active, type, progress }) {
      state.countdownActive = active === undefined ? state.countdownActive : active
      state.countdownType = type === undefined ? state.countdownType : type
      state.countdownProgress = progress === undefined ? state.countdownProgress : progress
    }
  },

  actions: {
    async ensureMicPermission ({ state, commit }) {
      if (state.micGranted) return true

      const result = await probeMicPermission()
      if (result === 'granted') {
        commit('SET_MIC_GRANTED', true)
        commit('SET_MIC_DENIED', false)
        return true
      }

      commit('SET_MIC_DENIED', result === 'denied')
      return false
    },

    // Only establishes permission; capture is per attempt in micStream.js.
    async setSpeakingMode ({ state, commit, dispatch }, enabled) {
      if (state.speakingMode === !!enabled) return state.speakingMode

      if (!enabled) {
        commit('SET_SPEAKING_MODE', false)
        commit('SET_VOICE_DETECTED', false)
        return false
      }

      const granted = await dispatch('ensureMicPermission')
      if (!granted) return false

      commit('SET_SPEAKING_MODE', true)
      return true
    },

    async toggleSpeakingMode ({ state, dispatch }) {
      return dispatch('setSpeakingMode', !state.speakingMode)
    },

    setRecognizedText ({ commit }, text) {
      commit('SET_RECOGNIZED_TEXT', text)
    },

    async clearRecognizedText ({ commit }) {
      commit('SET_RECOGNIZED_TEXT', '')
      return Promise.resolve()
    },

    setMicActive ({ commit }, active) {
      commit('SET_MIC_ACTIVE', active)
      if (!active) commit('SET_VOICE_DETECTED', false)
    },

    async startCountdown ({ commit, state }, type) {
      commit('SET_COUNTDOWN', {
        active: true,
        type: type,
        progress: 0
      })

      return new Promise(resolve => {
        const startTime = performance.now()

        const animate = (timestamp) => {
          const elapsed = timestamp - startTime
          const progress = Math.min(elapsed / state.countdownDuration, 1)

          commit('SET_COUNTDOWN', { progress })

          if (progress < 1 && state.countdownActive) {
            requestAnimationFrame(animate)
          } else {
            commit('SET_COUNTDOWN', { active: false })
            resolve()
          }
        }

        requestAnimationFrame(animate)
      })
    }
  },

  getters: {
    remainingCountdownSeconds: state => {
      if (!state.countdownActive) return 0
      return Math.ceil((state.countdownDuration - (state.countdownDuration * state.countdownProgress)) / 1000)
    },

    isRecognitionActive: state => state.micActive
  }
}
